import type { EarTrainingSessionStats } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import type { SubMode } from './ear-training-params.js'
import styles from './ear-training-summary.module.css'

const SUB_MODE_LABEL: Record<SubMode, string> = {
  interval: 'Interval ID',
  quality: 'Chord-quality ID',
}

export interface EarTrainingSummaryProps {
  subMode: SubMode
  stats: EarTrainingSessionStats
  onRestart: () => void
  onSwitchMode: (lastSubMode: SubMode) => void
}

/**
 * Ear Training Suite end-of-session summary. Shows the sub-mode label + total
 * score + a pair of CTAs ("Start new session" reruns the same sub-mode;
 * "Try other sub-mode" returns to the welcome screen with the *other*
 * card pre-selected via the host).
 *
 * Per Bowie §Q5 the sub-mode is locked per session; switching always re-enters
 * the welcome screen rather than swapping in-place.
 *
 * Spec: docs/superpowers/specs/2026-05-20-ear-training-suite-design.md §4.3
 */
export const EarTrainingSummary: FC<EarTrainingSummaryProps> = props => {
  const { subMode, stats, onRestart, onSwitchMode } = props
  return (
    <section className={styles.summary} aria-label="Session complete">
      <h2 className={styles.title}>Session complete</h2>
      <p className={styles.subMode}>{SUB_MODE_LABEL[subMode]}</p>
      <p className={styles.score}>
        {stats.correct} / {stats.total}
      </p>
      <div className={styles.actions}>
        <Button variant="contained" color="primary" size="large" onClick={onRestart}>
          Start new session
        </Button>
        <Button variant="outlined" size="large" onClick={() => onSwitchMode(subMode)}>
          Try other sub-mode
        </Button>
      </div>
    </section>
  )
}
